// Unpublished Work © 2023-2026 Deere & Company. All Worldwide Rights Reserved.
// THIS MATERIAL IS THE PROPERTY OF DEERE & COMPANY.
// ALL USE, ALTERATIONS AND/OR REPRODUCTION NOT SPECIFICALLY
// AUTHORIZED BY DEERE & COMPANY IS PROHIBITED.
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import AddButton from 'Ui/components/settings/common/add-button';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {getManufacturers} from 'Services/inventory-service';
import {deleteManufacturer} from 'Services/manufacturer-service';
import {getInventories} from 'Services/membership-service';
import {fetchEffectData, useDeepMemo} from 'Utils/react-utils';
import {capitalizeFirstLetter, replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import dialogTypes from 'Ui/components/common/dialog-types';
import SwitchInput from 'Ui/components/common/form/switch-input';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {MANAGE_EQUIPMENT_SETTINGS} from 'Common/constants/business-activities';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function isManufacturerModifiable(manufacturerInventoryId, membershipInventoryId, superUser) {
    return superUser || manufacturerInventoryId === membershipInventoryId;
}

function getDataGridColumns(translations, openManufacturerDialog, existingManufacturers, inventoryIdRef, onClose, superUser) {
    return [
        {
            headerName: translations.NAME,
            field: 'name',
            flex: 1,
            renderCell: (params) => {
                const {
                    row,
                    value
                } = params;

                if (isManufacturerModifiable(row.inventoryId, inventoryIdRef.current, superUser)) {
                    return (
                        <EditLink
                            onClick={() => openManufacturerDialog({
                                existingManufacturers,
                                inventoryIdRef,
                                manufacturer: row,
                                onClose,
                                translations
                            })}
                        >
                            {value}
                        </EditLink>
                    );
                }

                return value;
            }
        }
    ];
}

function getFilterComponent(translations, openManufacturerDialog, existingManufacturers, inventoryIdRef, onClose, showOnlyMyManufacturers, setShowOnlyMyManufacturers) {
    return (
        <div className='extra-table-options'>
            <SwitchInput
                checked={showOnlyMyManufacturers}
                name='showOnlyMyManufacturers'
                offLabel={`${capitalizeFirstLetter(translations.ONLINK_ONLY_MY_MANUFACTURERS)}`}
                onChange={() => setShowOnlyMyManufacturers(!showOnlyMyManufacturers)}
                translations={translations}
            />
            <AddButton
                label={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                    '0': translations.MANUFACTURER
                })}
                onClick={() => openManufacturerDialog({
                    existingManufacturers,
                    inventoryIdRef,
                    manufacturer: null,
                    onClose,
                    translations
                })}
            />
        </div>
    );
}

function ManufacturerSettings(props) {
    const {
        closeConfirmation,
        openConfirmation,
        openManufacturerDialog,
        superUser,
        translations
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(true);
    const [manufacturers, setManufacturers] = React.useState({
        allManufacturers: [],
        membershipManufacturers: []
    });
    const inventoryIdRef = React.useRef(null);
    const [showOnlyMyManufacturers, setShowOnlyMyManufacturers] = React.useState(true);

    const existingManufacturers = useDeepMemo(() => manufacturers.allManufacturers.reduce((manufacturerNames, manufacturer) => {
        manufacturerNames.add(manufacturer.name);

        return manufacturerNames;
    }, new Set()), [manufacturers]);

    async function fetchManufacturers(isMounted = () => true) {
        setLoading(true);

        const {manufacturers} = await getManufacturers();

        if (isMounted()) {
            const filteredManufacturers = manufacturers.filter(({name}) => name);

            setManufacturers({
                allManufacturers: filteredManufacturers,
                membershipManufacturers: filteredManufacturers
                    .filter(({inventoryId}) => inventoryId === inventoryIdRef.current)
            });
            setLoading(false);
        }
    }

    function deleteManufacturerHandler(manufacturer) {
        const lowerCaseManufacturer = translations.MANUFACTURER.toLowerCase();

        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': lowerCaseManufacturer
            }),
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.MANUFACTURER
            }),
            async onContinue() {
                try {
                    await deleteManufacturer(manufacturer.manufacturerId);
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': lowerCaseManufacturer
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }

                closeConfirmation();
                fetchManufacturers();
            }
        });
    }

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const {inventories} = await getInventories();

        inventoryIdRef.current = inventories?.length ? inventories[0].inventoryId : null;

        fetchManufacturers(isMounted);
    }), []);

    return (
        <div
            className={'settings-container data-grid'}
            data-testid='settings-container'
        >
            <DataGridWrapper
                columns={getDataGridColumns(
                    translations,
                    openManufacturerDialog,
                    existingManufacturers,
                    inventoryIdRef,
                    fetchManufacturers,
                    superUser
                )}
                deleteFilter={(manufacturer) => isManufacturerModifiable(manufacturer.inventoryId, inventoryIdRef.current, superUser)}
                deleteHandler={deleteManufacturerHandler}
                filterComponent={getFilterComponent(
                    translations,
                    openManufacturerDialog,
                    existingManufacturers,
                    inventoryIdRef,
                    fetchManufacturers,
                    showOnlyMyManufacturers,
                    setShowOnlyMyManufacturers
                )}
                getRowId={(row) => row.manufacturerId}
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: 'name',
                                sort: 'asc'
                            }
                        ]
                    }
                }}
                loading={loading}
                noDataAvailableMessage={translations.ONLINK_NO_MANUFACTURERS_FOUND}
                rows={showOnlyMyManufacturers ? manufacturers.membershipManufacturers : manufacturers.allManufacturers}
                searchFields={['name']}
                searchable={true}
                showDeleteColumn={isAuthorized({
                    myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                }, props)}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

ManufacturerSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    isMigrated: PropTypes.bool,
    myJdPermissions: PropTypes.myJdPermissions,
    openConfirmation: PropTypes.func,
    openManufacturerDialog: PropTypes.func,
    superUser: PropTypes.bool,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions,
        translations: state.translations,
        superUser: state.account.superUser
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openManufacturerDialog(props) {
            dispatch(openDialog(dialogTypes.MANUFACTURER_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ManufacturerSettings);
